import { useFormContext } from "../../context/FormContext";
import { useNavigate } from "react-router-dom";
import { navigateToSearchResults } from "../../utils/urlParams";
import { registerSearch } from "../../api/services/carService";
import { useCurrency } from "../../context/CurrencyContext";
import { stringToDate } from "./formUtils";

/**
 * useFormSubmit
 *
 * Owns all search form submission logic:
 * - Validation (location, dates, age)
 * - Auto-select top suggestion if user typed but didn't click
 * - Builds the final searchData object
 * - Calls updateSearchURL to navigate
 *
 * Returns: { handleSubmit }
 */
const useFormSubmit = ({
  draftPickup,
  draftDropoff,
  draftPickupDate,
  draftDropoffDate,
  draftPickupTime,
  draftDropoffTime,
  draftSameLocation,
  draftDriverCountry,
  isAgeDefault,
  customAge,
  isTestMode,
  onSearch,
  onClose,
  setValidationErrors,
}) => {
  const {
    pickupLocationObj,
    setPickupLocationObj,
    pickupGDSCode,
    setPickupGDSCode,
    dropoffLocationObj,
    setDropoffLocationObj,
    dropOffGDSCode,
    setDropOffGDSCode,
    setDriverAge,
    setDriverCountry,
    setUserManuallySelectedCountry,
    fetchLocationSuggestions,
  } = useFormContext();

  const navigate = useNavigate();
  const { selectedCurrency } = useCurrency();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationErrors({});

    // --- Age resolution ---
    const defaultAgeRange = ["US", "CA", "MX"].includes(draftDriverCountry)
      ? "26-70"
      : "30-65";
    let finalAge = isAgeDefault ? defaultAgeRange : customAge;

    if (!isAgeDefault) {
      const ageNum = parseInt(customAge);
      if (!customAge || isNaN(ageNum)) {
        alert("Please enter the driver's age.");
        return;
      }
      if (ageNum < 21) {
        alert("Driver's age must be at least 21 years.");
        setValidationErrors((prev) => ({ ...prev, driverAge: true }));
        return;
      }
      if (ageNum > 99) {
        alert("Driver's age must be between 21 and 99.");
        setValidationErrors((prev) => ({ ...prev, driverAge: true }));
        return;
      }
      finalAge = customAge;
    }

    // --- Pickup code resolution ---
    // If user typed but never clicked a suggestion, auto-select the top result
    let resolvedPickupCode = pickupLocationObj?.location_id || pickupGDSCode;

    if (draftPickup && !resolvedPickupCode) {
      const suggestions = await fetchLocationSuggestions(draftPickup);
      if (suggestions && suggestions.length > 0) {
        const best = suggestions[0];
        resolvedPickupCode = best.location_id || best.gdsCode;
        setPickupLocationObj(best);
        setPickupGDSCode(resolvedPickupCode);
        if (draftSameLocation) {
          setDropoffLocationObj(best);
          setDropOffGDSCode(resolvedPickupCode);
        }
      }
    }

    // --- Validation guards ---
    if (!draftPickup || !resolvedPickupCode) {
      alert("Please select a pickup location.");
      return;
    }

    // pickupLocationObj is not required — after a page refresh the URL sync
    // restores pickupGDSCode but not the full object (it's not a URL param).
    // resolvedPickupCode above already captures pickupLocationObj?.location_id
    // || pickupGDSCode, so a valid code is sufficient.

    if (!draftSameLocation && !draftDropoff) {
      alert("Please select a drop-off location.");
      return;
    }

    // Same rationale for dropoffLocationObj — dropOffGDSCode alone is sufficient.
    // After a refresh the URL sync restores the code but not the full object.
    if (!draftSameLocation && !dropoffLocationObj && !dropOffGDSCode) {
      alert("Please select a valid drop-off location.");
      return;
    }

    if (!draftPickupDate) {
      alert("Pick-up date is required.");
      return;
    }

    if (!draftDropoffDate) {
      alert("Drop-off date is required.");
      return;
    }

    if (!draftPickupTime) {
      alert("Pick-up time is required.");
      return;
    }

    if (!draftDropoffTime) {
      alert("Drop-off time is required.");
      return;
    }

    // --- Date range check ---
    const pickupDateObj = stringToDate(draftPickupDate);
    const dropoffDateObj = stringToDate(draftDropoffDate);

    if (dropoffDateObj < pickupDateObj) {
      alert("Drop-off date must be on or after the pick-up date.");
      setValidationErrors((prev) => ({
        ...prev,
        dropoffDate: "Invalid date range",
      }));
      return;
    }

    // --- Resolve dropoff code ---
    const resolvedDropoffCode = draftSameLocation
      ? resolvedPickupCode
      : dropoffLocationObj?.location_id || dropOffGDSCode || "";

    const isActuallySameLocation =
      draftSameLocation ||
      (resolvedPickupCode === resolvedDropoffCode && resolvedPickupCode !== "");

    // --- Sync context before navigating ---
    setDriverAge(finalAge);
    setDriverCountry(draftDriverCountry);
    setUserManuallySelectedCountry(true);

    // --- Build search payload ---
    const searchData = {
      sameLocation: isActuallySameLocation,
      pickupLocation: draftPickup,
      dropoffLocation: isActuallySameLocation ? draftPickup : draftDropoff,
      pickupCode: resolvedPickupCode,
      dropoffCode: resolvedDropoffCode,
      pickupDate: draftPickupDate,
      pickupTime: draftPickupTime,
      dropoffDate: draftDropoffDate,
      dropoffTime: draftDropoffTime,
      driverCountry: draftDriverCountry,
      driverAge: finalAge,
      is_test: isTestMode,
    };

    sessionStorage.setItem("returningFromSearch", "true");
    sessionStorage.removeItem("pageWasRefreshed");
    localStorage.setItem("is_own_search", "true");

    const registerPayload = {
      pickup_location_id: resolvedPickupCode,
      dropoff_location_id: resolvedDropoffCode,
      pickup_location_name: draftPickup,
      dropoff_location_name: isActuallySameLocation
        ? draftPickup
        : draftDropoff,
      pickup_datetime: `${draftPickupDate}T${draftPickupTime}:00`,
      dropoff_datetime: `${draftDropoffDate}T${draftDropoffTime}:00`,
      age: parseInt(finalAge) || 35,
      residence: draftDriverCountry,
      currency: selectedCurrency?.code || "USD",
      is_test: isTestMode,
    };

    const registerResponse = await registerSearch(registerPayload);
    const searchId = registerResponse?.search_id;

    if (!searchId) {
      console.error("registerSearch did not return a search_id");
      alert("Something went wrong. Please try again.");
      return;
    }

    localStorage.setItem("pickup_location_name", draftPickup);
    localStorage.setItem(
      "dropoff_location_name",
      isActuallySameLocation ? draftPickup : draftDropoff,
    );
    sessionStorage.setItem("search_pickupDate", draftPickupDate);
    sessionStorage.setItem("search_dropoffDate", draftDropoffDate);
    sessionStorage.setItem("search_pickupTime", draftPickupTime);
    sessionStorage.setItem("search_dropoffTime", draftDropoffTime);
    sessionStorage.setItem("search_driverAge", finalAge);
    sessionStorage.setItem("search_driverCountry", draftDriverCountry);
    // Clear stale search_id_currency so results page does not detect false currency change
    localStorage.removeItem("search_id_currency");

    navigateToSearchResults(navigate, searchId);
    if (onSearch) onSearch();
    if (onClose) onClose();
  };;

  return { handleSubmit };
};

export default useFormSubmit;